import type { PhotosByEvent, Season, ViewerMode } from '../types';
import type { OrganizedSeasonData, SeasonCollection } from '../types/seasonTypes';
import { parseSeasonId } from './seasonDomain';
import { calculateSeasonStats } from './seasonHelpers';
import { sortSeasonsByYear } from './sortingUtils';

type SeasonIdentity = Pick<Season, 'id' | 'name' | 'year'>;

// The family view derives its containers from the filtered manifest alone:
// `seasons` comes from the unfiltered /seasons endpoint and exists to surface
// empty containers for admin work. A malformed season id is dropped rather than
// invented, matching tvPlaylist's degradation.
function selectSeasonsForMode(
  photosByEvent: PhotosByEvent,
  seasons: SeasonIdentity[],
  mode: ViewerMode
): SeasonIdentity[] {
  if (mode !== 'family') {
    return seasons;
  }

  const manifestSeasonIds = new Set(Object.values(photosByEvent).map(entry => entry.season));
  const manifestSeasons: SeasonIdentity[] = [];
  for (const seasonId of manifestSeasonIds) {
    const parsed = parseSeasonId(seasonId);
    if (parsed) {
      manifestSeasons.push({ id: seasonId, name: parsed.name, year: parsed.year });
    }
  }

  return manifestSeasons;
}

function organizeSeason(season: SeasonIdentity, photosByEvent: PhotosByEvent): OrganizedSeasonData {
  const seasonEvents: PhotosByEvent = {};
  for (const [eventName, eventData] of Object.entries(photosByEvent)) {
    if (eventData.season === season.id) {
      seasonEvents[eventName] = eventData;
    }
  }

  const stats = calculateSeasonStats(seasonEvents);

  return {
    seasonId: season.id,
    events: seasonEvents,
    isEmpty: stats.eventCount === 0,
    eventCount: stats.eventCount,
    photoCount: stats.photoCount
  };
}

/** Seasons newest first for the viewer's mode, each with its events and counts, plus library totals. */
export function organizeSeasonCollection(
  photosByEvent: PhotosByEvent,
  seasons: SeasonIdentity[],
  mode: ViewerMode
): SeasonCollection {
  const visibleSeasons = selectSeasonsForMode(photosByEvent, seasons, mode);
  const sortedSeasons = sortSeasonsByYear(visibleSeasons);
  const organizedSeasons = sortedSeasons.map(season => organizeSeason(season, photosByEvent));

  let totalEvents = 0;
  let totalPhotos = 0;
  for (const season of organizedSeasons) {
    totalEvents += season.eventCount;
    totalPhotos += season.photoCount;
  }

  return {
    seasons: organizedSeasons,
    totalSeasons: organizedSeasons.length,
    totalEvents,
    totalPhotos
  };
}
